import type { EvaluationVerdict } from "@constants/evaluationConstants";
import { environment } from "@environments/environment";
import axios from "axios";

export interface EvaluationGroup {
  id: string;
  name: string;
}

export interface GetEvaluationGroupsResponse {
  exam_evaluation_groups: EvaluationGroup[];
}

// Document interface
export interface Document {
  id: string;
  filename: string;
  signed_url: string;
}

// Sample answer interface
export interface SampleAnswer {
  id: string;
  sequence_number: number;
  document: Document;
}

// Question interface based on API response
export interface Question {
  id: string;
  sequence_number: number;
  min_marks: number;
  max_marks: number;
  document: Document;
  rubric_document: Document;
  sample_answers: SampleAnswer[];
}

// Previous evaluation result interface
export interface PreviousEvaluationResult {
  awarded_marks: number;
  feedbacks: string[];
  evaluation_verdict: EvaluationVerdict;
}

// Current evaluation interface
export interface CurrentEvaluation {
  awarded_marks: number;
  feedbacks: string[];
  evaluation_verdict: "CORRECT" | "INCORRECT" | "PARTIALLY_CORRECT" | "UNATTEMPTED";
}

// Question with evaluation data
export interface QuestionWithEvaluation {
  question: Question;
  page_mapping: number[];
  previous_evaluation_result: PreviousEvaluationResult | null;
  current_evaluation?: CurrentEvaluation | null;
}

// Question paper submission document
export interface QuestionPaperSubmissionDocument {
  question_paper_id: string;
  document: Document;
}

// Exam submission interface
export interface ExamSubmission {
  id: string;
  exam_session_id: string;
  submitted_by: string;
  created_by: number;
  question_paper_submissions_documents: QuestionPaperSubmissionDocument[];
}

// Main evaluation task response interface
export interface EvaluationTaskResponse {
  task_id: string;
  evaluation_type: string;
  exam_submission: ExamSubmission;
  questions: QuestionWithEvaluation[];
}

export interface SaveEvaluationResponse {
  message: string;
}

export interface SubmitTaskResponse {
  message: string;
}

export async function fetchEvaluationQuestion(token:any,examSessionId: any): Promise<EvaluationTaskResponse> {
  const apiInstance = axios.create({
    baseURL: environment?.apiURL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  try {
    const response = await apiInstance.get(
      `/api/v2/evaluation-task/assign/${examSessionId}`
    );
    if (response?.data) {
      return response?.data as EvaluationTaskResponse;
    } else {
      throw new Error("Failed to get data from the API");
    }
  } catch (error) {
    console.error("Error fetching data:", error);
    throw new Error(
      "Error fetching data: " +
        (error instanceof Error ? error.message : String(error))
    );
  }
}

export interface SaveQuestionEvaluationProps {
  taskId: string;
  questionId: string;
  awardedMarks: number;
  feedbacks: string[];
  verdict: string;
  token: string;
}

export async function saveQuestionEvaluation(
  evaluationData: SaveQuestionEvaluationProps
): Promise<SaveEvaluationResponse> {
  try {
    const response = await fetch(
      `${environment.apiURL}api/v2/evaluation-task/${evaluationData.taskId}/save-evaluation`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${evaluationData.token}`,
        },
        body: JSON.stringify({
          question_id: evaluationData.questionId,
          awarded_marks: evaluationData.awardedMarks,
          feedbacks: evaluationData.feedbacks,
          evaluation_verdict: evaluationData.verdict,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to save evaluation: ${response.statusText}`
      );
    }

    const responseText = await response.text();
    return { message: responseText || "Evaluation Saved Successfully" };
  } catch (error) {
    console.error("Error in saveQuestionEvaluation:", error);
    throw error;
  }
}

export interface SubmitTaskEvaluationProps {
  taskId: string;
  token: string;
}

export async function submitTaskEvaluation(
  evaluationData: SubmitTaskEvaluationProps
): Promise<SubmitTaskResponse> {
  try {
    const response = await fetch(
      `${environment.apiURL}api/v2/evaluation-task/${evaluationData.taskId}/submit`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${evaluationData.token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `Failed to submit task: ${response.statusText}`
      );
    }

    const responseText = await response.text();
    return { message: responseText || "Evaluation Submitted Successfully" };
  } catch (error) {
    console.error("Error in submitTaskEvaluation:", error);
    throw error;
  }
}

// Legacy function for backward compatibility (deprecated)
interface submitEvaluationProps {
  evaluationTaskId: string;
  marksAwarded: number;
  feedbacks: Array<string>;
  questionId: string;
  token: string;
  examSubmissionId: string;
}

export async function submitEvaluation(evaluationData: submitEvaluationProps) {
  try {
    const response = await fetch(
      `${environment.apiURL}api/v1/evaluation-task/${evaluationData.evaluationTaskId}/submit-evaluation`,
      {
        method: "POST",
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${evaluationData?.token}`,
        },
        body: JSON.stringify({
          awarded_marks: evaluationData.marksAwarded,
          feedbacks: evaluationData.feedbacks,
          question_id: evaluationData.questionId,
          exam_submission_id: evaluationData.examSubmissionId,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to submit evaluation: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error in submitEvaluation:", error);
    throw error;
  }
}

export async function unassignEvaluationTask() {
  const token = localStorage.getItem("accessToken");
  const apiInstance = axios.create({
    baseURL: environment?.apiURL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  try {
    const response = await apiInstance.post(
      `/api/v1/evaluation-task/unassign-from-me`
    );
    if (response.status === 200) {
      return true;
    } else {
      throw new Error("Failed to unassign question");
    }
  } catch (error) {
    alert("Error unassigning question: " + error);
    throw new Error(
      "Error unassigning question: " +
        (error instanceof Error ? error.message : String(error))
    );
  }
}

export async function assignEvaluatorPools(
  body: any,
  examSessionId: string,
  workFlowStateId: string
) {
  const token = localStorage.getItem("accessToken");
  const apiInstance = axios.create({
    baseURL: environment?.apiURL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  try {
    const response = await apiInstance.put(
      `api/v1/exam-sessions/${examSessionId}/workflow-states/${workFlowStateId}/pools`,
      body
    );
    if (response?.data) {
      return response?.data || {};
    } else {
      throw new Error("Failed to update evaluator pools");
    }
  } catch (error) {
    console.error("Error updating evaluator pools:", error);
    throw new Error(
      "Error updating evaluator pools: " +
        (error instanceof Error ? error.message : String(error))
    );
  }
}

export async function getExamSessionByEvaluator() {
  const token = localStorage.getItem("accessToken");
  const apiInstance = axios.create({
    baseURL: environment?.apiURL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  try {
    const response = await apiInstance.get(`/api/v1/exam-sessions/all/evaluator`);
    if (response?.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching exam sessions by evaluator", error);
    throw new Error(
      "Error fetching exam sessions by evaluator" +
        (error instanceof Error ? error.message : String(error))
    );
  }
}

export async function getAssignedTaskForEvaluator() {
  const token = localStorage.getItem("accessToken");
  const apiInstance = axios.create({
    baseURL: environment?.apiURL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  try {
    const response = await apiInstance.get(`/api/v2/evaluation-task/assigned`);
    if (response?.data) {
      return response.data;
    }
  } catch (error) {
    console.error("Error fetching assigned task for evaluator", error);
    throw new Error(
      "Error fetching assigned task for evaluator" +
        (error instanceof Error ? error.message : String(error))
    );
  }
}
